/**
 * Numeric clamp helpers — home for the general-purpose clamp used
 * across UI atoms, gauges, and the design-system preview pipeline.
 *
 * Consolidated from four near-identical inline copies (CompactNumberInput,
 * CreditScoreGauge, design-system/presets, dev workbench) per
 * G-dedup-clamp / DECISION 1.
 *
 * NaN handling: `clamp(NaN, …)` returns `min`. This matches the previous
 * CompactNumberInput guard exactly. The `clamp01` callers (gauge/presets/
 * workbench) previously returned NaN for NaN input; expressing clamp01 via
 * the guarded clamp shifts that edge to 0. No call site passes NaN (inputs
 * are normalized ratios and OKLab channel values, all finite) — see DECISION 1.
 *
 * EXCLUDED from this consolidation (left inline, domain-specific): the
 * brick-breaker physics clamp (finite-only, no token contract), the
 * gauge-geometry [300,850] score clamp, and clampScore/clampChannel/
 * clampRefreshTimeoutMs.
 */

/** Clamp `n` into `[min, max]`. Returns `min` when `n` is NaN. */
export function clampNumber(n: number, min: number, max: number): number {
    if (Number.isNaN(n)) {
        return min;
    }
    return Math.min(max, Math.max(min, n));
}

/** Clamp `x` into `[0, 1]`. */
export function clamp01(x: number): number {
    return clampNumber(x, 0, 1);
}
